<template>
  <div>
    <FullHeightRadialLoader :isLoading="isFetching" :loader-msg="$t('components:common.loading')"/>
    <v-container class="pa-5 facility-data" v-show="!isFetching">
      <v-tabs
          color="primary"
          centered
          ref="tabs"
          :show-arrows="showArrows"
          v-model="tab"
          class="mb-2"
      >
        <v-tab to="#base" class="mr-2">
          <v-icon class="mr-3">home</v-icon>
          {{ $t('book/public:qrcode.data.tab.base') }}
        </v-tab>

        <!-- if tab with map gets opened, we have to refresh the map or tiles don't load -->
        <v-tab to="#tech" class="mr-2">
          <v-icon class="mr-3">build</v-icon>
          {{ $t('book/public:qrcode.data.tab.technical') }}
        </v-tab>

        <v-tab to="#state" class="mr-2">
          <v-icon class="mr-3">insights</v-icon>
          {{ $t('book/public:qrcode.common.state') }}
        </v-tab>

        <v-tab to="#documentations" class="mr-2">
          <v-icon class="mr-3">description</v-icon>
          {{ $t('common:documentation_plural') }}
        </v-tab>

        <v-tab to="#process" class="mr-2">
          <v-icon class="mr-3">list</v-icon>
          {{ $t('book/public:qrcode.data.tab.results') }}
        </v-tab>

        <v-tab to="#job">
          <v-icon class="mr-3">line_weight</v-icon>
          {{ $t('book/public:qrcode.data.tab.job') }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="base" eager>
          <v-card elevation="0" flat tile>
            <v-card-text>
              <v-row class="ma-0">
                <v-col cols="12" sm="12" md="6">

                  <LabelField
                      :label="this.$t('common:name')"
                      :value="building.name"
                  />
                  <LabelField
                      :label="this.$t('common:object-number')"
                      :value="building.objectNumber"
                  />
                  <LabelField
                      :label="this.$t('common:owner')"
                      :value="owner"
                  />
                  <LabelField
                      :label="this.$t('common:operator')"
                      :value="operator"
                  />
                  <LabelField
                      :label="this.$t('common:street')"
                      :value="building.address_street"
                  />
                  <LabelField
                      :label="this.$t('common:city')"
                      :value="building.address_city"
                  />
                  <LabelField
                      :label="this.$t('common:zip')"
                      :value="building.address_zip"
                  />
                  <LabelField
                      label="Building province"
                      :value="province"
                  />
                  <LabelField
                      label="Building country"
                      :value="country"
                  />

                </v-col>
                <v-col cols="12" sm="12" md="6">
                  <LabelField
                      :label="this.$t('common:name')"
                      :value="facility.name"
                  />
                  <LabelField
                      :label="this.$t('common:facility-number')"
                      :value="facility.objectNumber"
                  />
                  <LabelField
                      :label="this.$t('book/public:qrcode.data.base.facility.stairway')"
                      :value="facility.stairway"
                  />
                  <LabelField
                      :label="this.$t('book/public:qrcode.data.base.facility.manager')"
                      :value="manager"
                  />

                  <DataTable
                      :title="this.$t('common:role_plural')"
                      :headers="roleTableHeaders"
                      :data="roles"
                      sort-by="rank"
                      :sort-desc="false"
                  />

                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item value="tech" eager>
          <v-card elevation="0" flat tile>
            <v-card-text>
              <v-row justify="center">
                <v-col cols="12" md="11" >
                  <v-alert
                      v-if="technicalInfo.reducedShaftHead || technicalInfo.reducedShaftPit"
                      outlined
                      type="warning"
                      prominent
                      border="left"
                      color="orange"
                  >
                    {{ getShaftMineWarningText }}
                  </v-alert>
                </v-col>
              </v-row>
              <v-row justify="space-around" align="center">
                <v-col cols="12" md="5">
                  <v-row align="center">
                    <v-col>
                      <v-tooltip bottom :disabled="!isLoggedIn">
                        <template v-slot:activator="{ on, attrs}">
                          <v-btn
                              color="primary"
                              rounded
                              elevation="0"
                              class="mr-3 mb-3 mb-md-0 white--text"
                              v-bind="attrs"
                              v-on="on"
                              @click="$router.push(`/${$store.state.manager.bookModuleUrl}/public/qrcode/${$route.params.code}/onboarding`)"
                              :disabled="!isLoggedIn"
                          >
                            {{ $t('book/public:qrcode.data.state.btn-record') }}
                          </v-btn>
                        </template>
                        <span>{{ $t('book/public:qrcode.data.state.onboarding-tooltip')}}</span>
                      </v-tooltip>
                    </v-col>
                    <v-col>
                      <p v-if="!isLoggedIn" class="mb-0">{{ $t('book/public:qrcode.data.state.login') }}</p>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" md="5" class="text-right">
                  <p v-if="technicalInfo.updatedOn" class="greyTint--text">{{ $t('common:last-updated-on') }} {{ technicalInfo.updatedOn }}</p>
                  <p v-if="technicalInfo.updatedByUsername" class="greyTint--text">{{ $t('book/public:qrcode.data.state.last-updated-by') }} {{ technicalInfo.updatedByUsername }}</p>
                  <p v-if="!technicalInfo.updatedOn && !technicalInfo.updatedByUsername" class="scaleOrange--text mb-0">{{ $t('book/public:qrcode.data.state.not-done') }}</p>
                </v-col>
              </v-row>
              <v-row justify="space-around" v-show="technicalInfo.updatedOn">
                <v-col cols="12" md="5">

                  <!-- Typ of Facility Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('common:type') }}</h4>
                  <LabelField
                      :label="$t('book/technical-info:labels.elevator-type')"
                      :value="technicalInfo.type ? $t('enums:technical.elevator-type.' + technicalInfo.type) : ''"
                  />

                  <v-checkbox
                      v-model="technicalInfo.firefighterLift"
                      :label="$t('book/technical-info:labels.firefighters-lift')"
                      disabled
                  />

                  <h4 class="primary--text mb-5">{{ $t('book/technical-info:titles.connected-facilities') }}</h4>
                  <v-card outlined class="mb-5">
                    <v-virtual-scroll
                        :items="technicalInfo.mergedConnectedFacilities"
                        height="200"
                        item-height="64"
                    >
                      <template v-slot:default="{ item }">
                        <v-list-item>
                          {{ item.name }}
                        </v-list-item>
                      </template>
                    </v-virtual-scroll>
                  </v-card>

                  <!-- Transport Section  -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.transport') }}</h4>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <v-checkbox
                          v-model="technicalInfo.transportPerson"
                          :label="$t('book/technical-info:labels.transport-person')"
                          disabled
                      />
                      <v-checkbox
                          v-model="technicalInfo.transportFreight"
                          :label="$t('book/technical-info:labels.transport-freight')"
                          disabled
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <v-checkbox
                          v-model="technicalInfo.transportCar"
                          :label="$t('book/technical-info:labels.transport-car')"
                          disabled
                      />
                      <v-checkbox
                          v-model="technicalInfo.transportBed"
                          :label="$t('book/technical-info:labels.transport-bed')"
                          disabled
                      />
                    </v-col>
                  </v-row>

                  <!-- Story Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.story') }}</h4>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.lane-start')"
                          :value="technicalInfo.laneStart"
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.lane-end')"
                          :value="technicalInfo.laneEnd"
                      />
                    </v-col>
                  </v-row>

                  <LabelField
                      :label="$t('book/technical-info:labels.stations')"
                      :value="technicalInfo.stations"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.loading-stations')"
                      :value="technicalInfo.loadingStations"
                  />

                  <!-- Key-Safe Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.key-safe') }}</h4>
                  <v-checkbox
                      v-model="technicalInfo.keySafeExists"
                      :label="$t('book/technical-info:labels.key-safe-exists')"
                      disabled
                  />

                  <LabelField
                      v-if="technicalInfo.keySafeExists"
                      :label="$t('book/technical-info:labels.key-safe-info')"
                      :value="technicalInfo.keySafeInfo"
                  />

                  <!-- Engine Room Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.engine') }}</h4>
                  <v-checkbox
                      v-model="technicalInfo.engineRoomExists"
                      :label="$t('book/technical-info:labels.engine-room-exists')"
                      disabled
                  />

                  <LabelField
                      v-if="technicalInfo.engineRoomExists"
                      :label="$t('book/technical-info:labels.engine-room-position')"
                      :value="technicalInfo.engineRoomPosition ?
                    $t('enums:technical.engine-room-position.' + technicalInfo.engineRoomPosition) : ''"
                  />

                  <LabelField
                      v-if="technicalInfo.engineRoomExists"
                      :label="$t('book/technical-info:labels.engine-room-position-info')"
                      :value="technicalInfo.engineRoomPositionInfo"
                  />

                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.cabin') }}</h4>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.cabin-width')"
                          :value="technicalInfo.cabinWidth + ' mm'"
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.cabin-depth')"
                          :value="technicalInfo.cabinDepth + ' mm'"
                      />
                    </v-col>
                  </v-row>

                  <h6 class="primary--text font-weight-medium my-1">{{ $t('book/technical-info:titles.cabin-door') }}</h6>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('common:type')"
                          :value="technicalInfo.cabinDoorType ? $t('enums:technical.elevator-door-types.' + technicalInfo.cabinDoorType) : ''"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.cabin-door-width')"
                          :value="technicalInfo.cabinDoorWidth + ' mm'"
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.cabin-door-height')"
                          :value="technicalInfo.cabinDoorHeight + ' mm'"
                      />
                    </v-col>
                  </v-row>

                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.shaft') }}</h4>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.shaft-protection-type')"
                          :value="technicalInfo.shaftProtectionType ? $t('enums:technical.elevator-protection-types.' + technicalInfo.shaftProtectionType) : ''"
                      />
                    </v-col>
                  </v-row>

                  <h6 class="primary--text font-weight-medium my-1">{{ $t('book/technical-info:titles.shaft-door') }}</h6>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('common:type')"
                          :value="technicalInfo.shaftDoorType ? $t('enums:technical.elevator-door-types.' + technicalInfo.shaftDoorType) : ''"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.shaft-door-width')"
                          :value="technicalInfo.shaftDoorWidth + ' mm'"
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.shaft-door-height')"
                          :value="technicalInfo.shaftDoorHeight + ' mm'"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" md="5">
                  <!-- Technical informations Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('book/technical-info:titles.tech') }}</h4>
                  <LabelField
                      :label="$t('book/technical-info:labels.drive')"
                      :value="technicalInfo.drive ? $t('enums:technical.drive.' + technicalInfo.drive) : ''"
                  />

                  <LabelField
                      v-if="technicalInfo.drive === 'HYDRAULIC'"
                      :label="$t('book/technical-info:labels.hydraulic-drive')"
                      :value="technicalInfo.hydraulicDriveType ? $t('enums:technical.elevator-hydraulic-drive-types.' + technicalInfo.hydraulicDriveType) : ''"
                  />

                  <LabelField
                      v-else-if="technicalInfo.drive === 'ROPE'"
                      :label="$t('book/technical-info:labels.rope-drive')"
                      :value="technicalInfo.ropeDriveType ? $t('enums:technical.elevator-rope-drive-types.' + technicalInfo.ropeDriveType) : ''"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.door-position')"
                      :value="technicalInfo.doorPosition ?
                    $t('enums:technical.elevator-door-position.' + technicalInfo.doorPosition) : ''"
                  />


                  <LabelField
                      :label="$t('book/technical-info:labels.max-load')"
                      :value="technicalInfo.maxLoad"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.max-persons')"
                      :value="technicalInfo.maxPersons"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.operating-speed')"
                      :value="technicalInfo.operatingSpeed"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.cabin-height')"
                      :value="technicalInfo.cabinHeight"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.controls')"
                      :value="technicalInfo.control"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.frequency-converter')"
                      :value="technicalInfo.frequencyConverter"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.door-security')"
                      :value="technicalInfo.doorSecurityType ? $t('enums:technical.elevator-door-security-types.' + technicalInfo.doorSecurityType) : ''"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.manufacturer')"
                      :value="manufacturer"
                  />

                  <v-checkbox
                      v-model="technicalInfo.awmModule"
                      :label="$t('book/technical-info:labels.AWM-module')"
                      disabled
                  />

                  <v-checkbox
                      v-model="technicalInfo.knownOperator"
                      :label="$t('book/technical-info:labels.known-operator')"
                      disabled
                  />

                  <!-- Installation -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.installation') }}</h4>

                  <LabelField
                      :label="$t('book/technical-info:labels.build-year')"
                      :value="technicalInfo.buildYear"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.building-permit')"
                      :value="technicalInfo.buildingPermit"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.permission-date')"
                      :value="technicalInfo.permissionToUse"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.date-modernized')"
                      :value="technicalInfo.modernizedOn"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.assembler')"
                      :value="assembler"
                  />

                  <!-- Emergency Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.emergency') }}</h4>
                  <ul>
                    <li v-for="emergency in technicalInfo.emergencyType" :key="emergency">
                      <p class="pb-0">
                        {{ $t('enums:technical.elevator-emergency-types.' + emergency) }}
                      </p>
                    </li>
                  </ul>
                  <v-checkbox
                      v-model="technicalInfo.emergencyCallSystemGSM"
                      :label="$t('book/technical-info:labels.emergency-GSM')"
                      disabled
                  />

                  <v-checkbox
                      v-if="technicalInfo.emergencyCallSystemGSM"
                      v-model="technicalInfo.emergencyAnalog"
                      :label="$t('book/technical-info:labels.emergency-analog')"
                      disabled
                  />

                  <LabelField
                      v-if="technicalInfo.emergencyCallSystemGSM"
                      :label="$t('book/technical-info:labels.emergency-phone')"
                      :value="technicalInfo.emergencyPhoneNumber"
                  />

                  <LabelField
                      v-if="technicalInfo.emergencyCallSystemGSM"
                      :label="$t('book/technical-info:labels.emergency-info')"
                      :value="technicalInfo.emergencyCallSystemInfo"
                  />

                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item value="state" eager>
          <v-card elevation="0" flat tile>
            <v-card-text>
              <v-row justify="space-around" align="center">
                <v-col cols="12" md="5">
                  <v-row align="center">
                    <v-col>
                      <v-tooltip bottom :disabled="!isLoggedIn">
                        <template v-slot:activator="{ on, attrs}">
                          <v-btn
                              color="primary"
                              rounded
                              elevation="0"
                              class="mr-3 mb-3 mb-md-0 white--text"
                              v-bind="attrs"
                              v-on="on"
                              @click="$router.push(`/${$store.state.manager.bookModuleUrl}/public/qrcode/${$route.params.code}/onboarding`)"
                              :disabled="!isLoggedIn"
                          >
                            {{ $t('book/public:qrcode.data.state.btn-record') }}
                          </v-btn>
                        </template>
                        <span>{{ $t('book/public:qrcode.data.state.onboarding-tooltip')}}</span>
                      </v-tooltip>
                    </v-col>
                    <v-col>
                      <p v-if="!isLoggedIn" class="mb-0">{{ $t('book/public:qrcode.data.state.login') }}</p>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" md="5" class="text-right">
                  <p v-if="state.savedOn" class="greyTint--text">{{ $t('common:last-updated-on') }} {{ state.savedOn }}</p>
                  <p v-if="state.savedByUsername" class="greyTint--text">{{ $t('book/public:qrcode.data.state.last-updated-by') }} {{ state.savedByUsername }}</p>
                  <p v-if="!state.savedOn && !state.savedByUsername" class="scaleOrange--text mb-0">{{ $t('book/public:qrcode.data.state.not-done') }}</p>
                </v-col>
              </v-row>

              <!-- watch on openedPanel() determines image content to load, so ordering of
                  v-expansion-panel's must correspond to openedPanel()  -->
              <v-expansion-panels flat tile v-show="state.savedOn" class="tab-elevation" v-model="openedPanel">
                <v-row justify="center">
                  <v-col cols="12" lg="10">

                    <!-- #0 On-site facility documentations -->
                    <v-expansion-panel class="my-3">
                      <v-expansion-panel-header class="pa-3">
                        <h6 class="mb-0 ml-2 greyDark--text">{{ $t('book/public:qrcode.data.state.panel-titles.docu') }}</h6>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content eager class="mt-2">
                        <RuleStateTable
                            ref="ruleStateTable"
                            :header="docuHeaders"
                            read-only
                        />
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- #1 Car -->
                    <v-expansion-panel class="my-3">
                      <v-expansion-panel-header class="pa-3">
                        <h6 class="mb-0 ml-2 greyDark--text">{{ $t('book/public:qrcode.data.state.panel-titles.car') }}</h6>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="mt-2">
                        <StateTable :data="state" :header="docuHeaders" :fields="carHeaders" ref="stateTableCar" readOnly/>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- #2 On every stop -->
                    <v-expansion-panel class="my-3">
                      <v-expansion-panel-header class="pa-3">
                        <h6 class="mb-0 ml-2 greyDark--text">{{ $t('book/public:qrcode.data.state.panel-titles.stop') }}</h6>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="mt-2">
                        <StateTable :data="state" :header="docuHeaders" :fields="stopHeaders" ref="stateTableStop" readOnly/>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- #3 Engine room -->
                    <v-expansion-panel class="my-3">
                      <v-expansion-panel-header class="pa-3">
                        <h6 class="mb-0 ml-2 greyDark--text">{{ $t('book/public:qrcode.data.state.panel-titles.engine-room') }}</h6>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="mt-2">
                        <StateTable :data="state" :header="docuHeaders" :fields="erHeaders" ref="stateTableEr" readOnly/>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- #4 Shaft / car roof -->
                    <v-expansion-panel class="my-3">
                      <v-expansion-panel-header class="pa-3">
                        <h6 class="mb-0 ml-2 greyDark--text">{{ $t('book/public:qrcode.data.state.panel-titles.shaft') }}</h6>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="mt-2">
                        <StateTable :data="state" :header="docuHeaders" :fields="shaftHeaders" ref="stateTableShaft" readOnly/>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- #5 Shaft pit -->
                    <v-expansion-panel class="my-3">
                      <v-expansion-panel-header class="pa-3">
                        <h6 class="mb-0 ml-2 greyDark--text">{{ $t('book/public:qrcode.data.state.panel-titles.shaft-pit') }}</h6>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="mt-2">
                        <StateTable :data="state" :header="docuHeaders" :fields="shaftPitHeaders" ref="stateTableShaftPit" readOnly/>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- #6 Image Panel -->
                    <v-expansion-panel class="my-3">
                      <v-expansion-panel-header class="pa-3">
                        <h6 class="mb-0 ml-2 greyDark--text">{{ $t('book/public:qrcode.onboarding.image-section') }}</h6>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content eager class="mt-2">
                        <RuleStateTable
                            ref="pictureTable"
                            :header="[$t('common:name'), $t('common:image_plural')]"
                            read-only
                            is-picture-state
                        />
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- #7 Summary -->
                    <v-expansion-panel class="my-3">
                      <v-expansion-panel-header class="pa-3">
                        <h6 class="mb-0 ml-2 greyDark--text">{{ $t('book/public:qrcode.data.state.panel-titles.summary') }}</h6>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content eager class="mt-2">
                        <v-textarea
                            outlined
                            disabled
                            v-model="state.summary"
                        />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-col>
                </v-row>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- removed eager on v-tab-item documentations loads image-content lazy-->
        <v-tab-item value="documentations">
          <v-card>
            <v-card-text>
              <PublicDocuTab v-if="facility.id" :facility-id="facility.id"/>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item value="process" eager>
          <v-card elevation="0" flat tile>
            <v-card-text>
              <v-row class="ma-0">
                <v-col cols="12">
                  <DataTablePaginated
                      ref="tableProcessResult"
                      class="ma-1"
                      :title="$t('book/public:qrcode.data.process.process-table-title')"
                      :headers="processResultHeaders"
                      @updateTableData="updateProcessResultTable"
                      icon="remove_red_eye"
                      @clickedButton1="viewProcessResult"
                      sortBy="finishedOn"
                      sortDesc
                  />
                  <v-container
                      v-if="selectedProcessResult"
                  >
                    <v-row>
                      <v-col cols="12" sm="6" xs="12" class="py-0">
                        <LabelField
                            :label="$t('common:process')"
                            :value="selectedProcessResult.processName"
                        />

                        <LabelField
                            :label="$t('book/public:qrcode.data.process.user')"
                            :value="selectedProcessResult.username"
                        />

                        <LabelField
                            :label="$t('common:date')"
                            :value="selectedProcessResult.finishedOn"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" xs="12" class="py-0">
                        <LabelField
                            v-if="selectedProcessResult.rideCount"
                            :label="$t('book/public:qrcode.data.process.rides')"
                            :value="selectedProcessResult.rideCount"
                        />

                        <v-checkbox
                            disabled
                            v-model="selectedProcessResult.gpsChecked"
                            :label="$t('book/public:qrcode.data.process.gps-check')"
                            color="primary"
                        />
                      </v-col>
                    </v-row>

                    <DataTable
                        :headers="processStepResultHeaders"
                        :data="selectedProcessResult.stepResults"
                        :title="$t('common:process-step_plural')"
                        checkbox-on-property="success"
                        checkbox-on-property2="finished"
                    />

                  </v-container>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item value="job" eager>
          <v-card elevation="0" flat tile>
            <v-card-text>
              <v-row class="ma-0">
                <v-container
                    v-if="isLoggedIn"
                >
                  <v-row>
                    <v-col cols="12">
                      <DataTablePaginated
                          ref="tableOpenProcess"
                          class="ma-1"
                          :title="$t('book/public:qrcode.data.job.table.title')"
                          :headers="openProcessesHeaders"
                          @updateTableData="updateOpenProcessTable"
                          icon="open_in_new"
                          @clickedButton1="takeoverJob"
                          sortBy="date"
                          disableSearch
                      />

                      <ThreeButtonDialog
                          ref="takeoverJobButton"
                          color="grey"
                          :dialog-title="$t('book/public:qrcode.data.job.takeover-button.title')"
                          :dialog-text="$t('book/public:qrcode.data.job.takeover-button.text')"
                          :button1-text="$t('book/public:qrcode.data.job.takeover-button.accept')"
                          :button2-text="$t('book/public:qrcode.data.job.takeover-button.decline')"
                          :button3-text="$t('buttons:cancel')"
                          @clickedButton1="takeOverJobConfirmed(true)"
                          @clickedButton2="takeOverJobConfirmed(false)"
                          @clickedButton3="$refs.takeoverJobButton.showDialog = false"
                          :hide-button="true"
                      />

                      <v-col cols="12">
                        <h4 class="primary--text font-weight-medium mt-5 mt-xl-0">
                          {{ $t('book/public:qrcode.data.job.quickstart.title') }}</h4>
                        <v-row justify="center" align="center">
                          <v-col>
                            <v-select
                                :items="processes"
                                item-value="id"
                                item-text="name"
                                :menu-props="{ bottom: true, offsetY: true }"
                                v-model="selectedProcess"
                                outlined
                                :label="$t('common:process')"
                                clearable
                                :hide-details="true"
                            />
                          </v-col>
                          <v-col>
                            <v-btn
                                :disabled="!selectedProcess"
                                outlined
                                color="primary"
                                @click="quickstartProcess"
                            >
                              {{ $t('book/public:qrcode.data.job.quickstart.start') }}
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-col>

                    </v-col>
                  </v-row>
                </v-container>
                <v-container
                    v-else
                >
                  <v-row>
                    <v-col cols="12">
                      {{ $t('book/public:qrcode.data.job.login') }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-btn
                          color="primary"
                          @click="gotoLogin"
                      >Login</v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-container>
  </div>
</template>

<script>
import Facility from "@/modules/book/data/facility";
import Building from "@/modules/book/data/building";
import TechnicalInfo from "@/modules/book/data/technical-info";
import LabelField from "@/modules/core/components/LabelField";
import DataTablePaginated from "@/modules/core/components/DataTable/DataTablePaginated";
import DataTable from "@/modules/core/components/DataTable/DataTable";
import Utils from "@/modules/core/services/utils";
import {mapGetters} from "vuex";
import Vue from "vue";
import QRCodeService from "@/modules/book/services/qrcode-service";
import ThreeButtonDialog from "@/modules/core/components/TwoButtonDialog/ThreeButtonDialog";
import ProcessJobService from "@/modules/book/services/process/impl/process-job-service";
import ProcessService from "@/modules/book/services/process/impl/process-service";
import GPSUtils from "@/modules/core/services/gps-utils";
import DataUtils from "@/modules/core/services/data-utils";
import Process from "@/modules/book/data/process";
import StateTable from "@/modules/core/components/DataTable/StateTable";
import ElevatorState from "@/modules/book/data/elevator-state";
import StateTableUtils from "@/modules/core/services/state-table-utils";
import FacilityDocumentation from "@/modules/book/data/facility-documentation";
import EnumService from "@/modules/core/services/enum-service";
import FullHeightRadialLoader from "@/modules/core/components/Loader/FullHeightRadialLoader";
import RuleStateTable from "@/modules/core/components/DataTable/RuleStateTable";
import DocumentationService from "@/modules/core/services/documentation-service";
import PublicDocuTab from "@/modules/book/views/public/tabs/PublicDocuTab";
import DateUtils from "@/modules/core/services/date-utils";

export default {
  name: "QRCodeFacilityData",
  components: {
    PublicDocuTab,
    FullHeightRadialLoader,
    StateTable,
    LabelField,
    DataTable,
    DataTablePaginated,
    ThreeButtonDialog,
    RuleStateTable
  },
  data() {
    return {
      building: new Building(),
      facility: new Facility(),
      technicalInfo: new TechnicalInfo(),
      state: new ElevatorState(),
      facilityDocumentations: [],
      facilityPictures: [],
      docuFields: [],
      carHeaders: [],
      stopHeaders: [],
      erHeaders: [],
      shaftHeaders: [],
      shaftPitHeaders: [],

      owner: "",
      operator: "",
      manager: "",
      country: "",
      province: "",
      manufacturer: "",
      assembler: "",
      roles: [],

      processResults: [],
      selectedProcessResult: null,
      takeoverJobId: null,

      jobs: [],

      processes: [],
      selectedProcess: null,

      tab: null,
      showArrows: false,
      isFetching: true,
      openedPanel: undefined,
      ruleStatesLoaded: false,
      requiredPicturesLoaded: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn']),
    roleTableHeaders() {
      return [
        {text: this.$t('common:user'), value: "userName"},
        {text: this.$t('common:role'), value: "roleName"},
        {text: this.$t('common:rank'), value: "rank"},
      ]
    },
    processResultHeaders() {
      return [
        {text: this.$t('common:date'), value: "finishedOn"},
        {text: this.$t('common:process'), value: "processName"},
        {text: this.$t('book/public:qrcode.data.process.user'), value: "username"},
        {text: this.$t('book/public:qrcode.data.process.rides'), value: "rideCount"},
        {text: "", value: "controls"},
      ];
    },
    processStepResultHeaders() {
      return [
        {text: this.$t('common:name'), value: "name"},
        {text: this.$t('common:answer'), value: "answer"},
        {text: this.$t('book/public:qrcode.data.process.step-table.info'), value: "additionalAnswer"},
        {text: this.$t('common:success'), value: "checkbox"},
        {text: this.$t('book/public:qrcode.data.process.step-table.finished'), value: "checkbox2"},
      ];
    },
    openProcessesHeaders() {
      return [
        {text: this.$t('book/public:qrcode.data.job.table.deadline'), value: "date"},
        {text: this.$t('common:process'), value: "processName"},
        {text: this.$t('common:status'), value: "badge"},
        {text: this.$t('book/public:qrcode.data.job.table.charge'), value: "assignedToName"},
        {text: this.$t('common:role'), value: "assignedToRoleName"},
        {text: this.$t('book/public:qrcode.data.job.table.start'), value: "controls", sortable: false}
      ]
    },

    docuHeaders() {
      return [
        this.$t('book/public:qrcode.onboarding.docu-headers.check'),
        this.$t('common:status'),
        this.$t('common:comment'),
        this.$t('common:document_plural')
      ]
    },

    currentLang() {
      return this.$store.state.translation.language;
    },

    getShaftMineWarningText() {
      if (this.technicalInfo.reducedShaftHead && !this.technicalInfo.reducedShaftPit) {
        return this.$t('book/technical-info:shaft-mine.only-head');
      } else if (!this.technicalInfo.reducedShaftHead && this.technicalInfo.reducedShaftPit) {
        return this.$t('book/technical-info:shaft-mine.only-pit');
      } else if (this.technicalInfo.reducedShaftHead && this.technicalInfo.reducedShaftPit) {
        return this.$t('book/technical-info:shaft-mine.head-and-pit');
      } else {
        return ''
      }
    }
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.translateAndGetElevatorStateFields();
      });
    },
    /**
     * implement lazy loading of documents within facility-state
     *
     * @param panelNr   nr. of currently opened expansion-panel
     */
    openedPanel(panelNr) {
      // two differnt types of tables:
      // #0 RuleStates & #6 Image Panel: eager loading + setting data on $refs, when panel opened,
      //                                 checked here against double opening
      // #1 - #5 StateTable: lazy loading + calling loadDocumentsWithChecks() on $refs, when panel opened,
      //                     checked on StateTable against double opening

      // #0 On-site facility documentations
      if (panelNr === 0) {
        if (!this.ruleStatesLoaded) {
          this.getRuleDocumentations().then(() => {
            this.ruleStatesLoaded = true;
            this.$refs.ruleStateTable.facilityState = this.state;
            this.$refs.ruleStateTable.rulesOrPictures = this.state.ruleStates;
          });
        }
      }
      //  #1 Car
      else if (panelNr === 1) {
        console.log(this.$refs.stateTableCar);
        this.waitAndCheckForStateRef('stateTableCar');
      }
      // #2 On every stop
      else if (panelNr === 2) {
        this.waitAndCheckForStateRef('stateTableStop');
      }
      // #3 Engine room
      else if (panelNr === 3) {
        this.waitAndCheckForStateRef('stateTableEr');
      }
      //#4 Shaft / car roof
      else if (panelNr === 4) {
        this.waitAndCheckForStateRef('stateTableShaft');
      }
      //  #5 Shaft pit
      else if (panelNr === 5) {
        this.waitAndCheckForStateRef('stateTableShaftPit');
      }

      // #6 Image Panel
      else if (panelNr === 6) {
        if (!this.requiredPicturesLoaded) {
          this.getRequiredPictures().then(() => {
            this.requiredPicturesLoaded = true;
            this.$refs.pictureTable.facilityState = this.state;
            this.$refs.pictureTable.rulesOrPictures = this.facilityPictures;
          })
        }
      }
      //  #7 Summary
      else if (panelNr === 7) {
        //  no documents to load
      }
    },
  },

  methods: {
    waitAndCheckForStateRef(stateTableRefString) {
      const ref = this.$refs[stateTableRefString];
      if (this.isFetching || !ref) {  // if reference is not available yet (cause of non-eager loading), wait 100ms and try again
        Utils.sleep(100).then(() => {
          this.waitAndCheckForStateRef(stateTableRefString);
          return;
        });
      }
      else {
        // calls StateTable.loadDocumentsWithChecks()
        ref.loadDocumentsWithChecks();
      }
    },

    async getRuleDocumentations() {
      let promises = [];

      for (let rule of this.state.ruleStates) {
        if (rule.documents.length > 0) {
          for (let document of rule.documents) {
            promises.push(
                new Promise(resolve => {
                  DocumentationService.getDocByTypeAndIdForPublic(
                      document.id,
                      document.fileType,
                      document.documentType,
                      document.facilityId,
                      this.$route.params.code,
                      (res) => {
                        Vue.set(document, "src", res);
                        resolve(document)
                      },
                      (error) => {
                        this.$logger.error("Failed loading documentation source for document " + document.id +", because: " + error);
                      }
                  )
                })
            )
          }
        }
      }

      await Promise.all(promises)
    },

    async getRequiredPictures() {
      let promises = [];

      for (let i = 0; i < this.facilityPictures.length; i++) {
        this.facilityPictures[i].documents = [];

        for (let j = 0; j < this.facilityDocumentations.length; j++) {
          if (this.facilityPictures[i].name === this.facilityDocumentations[j].type) {
            Vue.set(this.facilityPictures[i].documents, this.facilityPictures[i].documents.length, this.facilityDocumentations[j])
          }
        }
      }

      for (let picture of this.facilityPictures) {
        if (picture.documents.length > 0) {
          for (let document of picture.documents) {
            promises.push(
                new Promise(resolve => {
                  DocumentationService.getDocByTypeAndIdForPublic(
                      document.id,
                      document.fileType,
                      document.documentType,
                      document.facilityId,
                      this.$route.params.code,
                      (res) => {
                        Vue.set(document, "src", res);
                        resolve(document)
                      },
                      (error) => {
                        this.$logger.error("Failed loading documentation source for document " + document.id +", because: " + error);
                      }
                  )
                })
            )

          }
        }
      }

      await Promise.all(promises)
    },

    initialise(data){
      this.building = data.building;
      this.facility = data.facility;
      this.technicalInfo = data.technicalInfo;
      this.owner = data.ownerName;
      this.operator = data.operatorName;
      this.manager = data.managerName;
      this.country = data.countryName;
      this.province = data.provinceName;
      this.manufacturer = data.manufacturerName;
      this.assembler = data.assemblerName;
      this.roles = data.facilityUserAssignment;
      this.processResults = data.processResult;
      this.state = data.state;
      this.facilityDocumentations = DataUtils.parseDataToObjectArray(data.facilityDocumentation, FacilityDocumentation);
      this.facilityPictures = data.requiredPictures;

      // format savedOn dateTime of documentations
      for (let i = 0; i < this.facilityDocumentations.length; i++) {
        this.facilityDocumentations[i].formattedSavedOn = DateUtils.parseDateTimeForDisplay(this.facilityDocumentations[i].savedOn);
      }

      EnumService.translateAndSortEnumObject(this.state.ruleStates, "enums:facility-documentation-types.")
      this.facilityPictures = EnumService.translateAndSortEnum(this.facilityPictures, "enums:facility-documentation-types.")

      this.translateAndGetElevatorStateFields();

      for (let i = 0; i < this.state.ruleStates.length; i++) {
        this.state.ruleStates[i].documents = [];

        for (let j = 0; j < this.facilityDocumentations.length; j++) {
          if (this.state.ruleStates[i].name === this.facilityDocumentations[j].type) {
            Vue.set(this.state.ruleStates[i].documents, this.state.ruleStates[i].documents.length, this.facilityDocumentations[j])
          }
        }
      }

      /*
      // have to wait for all documentations to be fully loaded
      // or otherwise the documentations will not show
      this.getRuleDocumentations().then(() => {
        this.$refs.ruleStateTable.facilityState = this.state;
        this.$refs.ruleStateTable.rulesOrPictures = this.state.ruleStates;

        this.getRequiredPictures().then(() => {
          this.$refs.pictureTable.facilityState = this.state;
          this.$refs.pictureTable.rulesOrPictures = this.facilityPictures;
        })
      })
//*/
      this.processResults.forEach((process) => {
        process.finishedOn = DateUtils.parseDateTimeForDisplay(process.finishedOn);
      })
      if(this.country){
        this.country = Utils.translateCountry(data.countryName);
      }
      if(this.province){
        this.province = Utils.translateProvinces(data.countryName,data.provinceName)
      }

      this.technicalInfo.mergedConnectedFacilities = [];
      for (let facility of this.technicalInfo.connectedFacilities) {
        this.technicalInfo.mergedConnectedFacilities.push(facility)
      }
      for (let facility of this.technicalInfo.connectedByFacilities) {
        this.technicalInfo.mergedConnectedFacilities.push(facility);
      }

      //only load process job information if user is logged in
      if(!this.isLoggedIn){
        this.isFetching = false;
        this.showArrows = true;
        return;
      }
      ProcessService.readAllFromAPI(
        (data) => {
          this.processes = DataUtils.parseDataToObjectArray(data.data, Process);
          this.isFetching = false;
          this.showArrows = true;
        },
        (error) => {
          this.$logger.error("Failed loading processes for code " + this.$route.params.code +", because: " + error);
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:process_plural')}))
        }
      )
    },

    translateAndGetElevatorStateFields() {
      EnumService.translateAndSortEnumObject(this.state.ruleStates, "enums:facility-documentation-types.")
      this.carHeaders = StateTableUtils.getElevatorStateCarFieldsAndTranslation();
      this.stopHeaders = StateTableUtils.getElevatorStateStopFieldsAndTranslation();
      this.erHeaders = StateTableUtils.getElevatorStateERFieldsAndTranslation();
      this.shaftHeaders = StateTableUtils.getElevatorStateShaftFieldsAndTranslation();
      this.shaftPitHeaders = StateTableUtils.getElevatorStateShaftPitFieldsAndTranslation();
    },

    viewProcessResult(item){
      this.selectedProcessResult = item;
    },

    gotoLogin(){
      window.location.replace(Vue.$keycloak.createLoginUrl());
    },

    takeoverJob(job){
      this.takeoverJobId = job.id;
      this.$refs.takeoverJobButton.showDialog = true;
    },

    takeOverJobConfirmed(GpsCheck){
      if(GpsCheck){
        this.$getLocation({
          enableHighAccuracy: true,
        }).then(coordinates => {
          var userLat = coordinates.lat;
          var userLng = coordinates.lng;

          var distance = GPSUtils.calcDistance(userLat, userLng, this.building.latitude, this.building.longitude);
          if(distance == -1){
            Utils.showFailureSnackbar(this.$t('utils:gps.missing-value'))
          }
          else if(distance < process.env.VUE_APP_CHECK_IN_LOCATION_RADIUS) {
            this.startProcessJob(true)
          } else {
            Utils.showFailureSnackbar(this.$t('utils:gps.distance-error'))
          }
        }).catch((error) => {
          this.$logger.info("Could not access GPS data, because: " + error);
          Utils.showFailureSnackbar(this.$t('utils:gps.access-error'))
        });
      }
      else {
        this.startProcessJob(false);
      }
      this.$refs.takeoverJobButton.showDialog = false;
    },

    /**
     * Will first takeover the job by assigining the logged in user to the process, then use the default
     * Process Start functionality.
     *
     * @param gpsCheckDone information about if gps check was done or not
     */
    startProcessJob(gpsCheckDone){
      //TODO store gpsCheckInformation
      this.$logger.info("GPSCheckState: " + gpsCheckDone);

      QRCodeService.takeoverJob(this.$route.params.code, this.takeoverJobId,
        () => {
          ProcessJobService.startProcessJob(this.takeoverJobId, gpsCheckDone,
            () => {
              this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/process/${this.takeoverJobId}`);
            },
            (error) => {
              this.$logger.info("Error at starting Process Job: " + error);
              Utils.showFailureSnackbar(this.$t('book/public:qrcode.snackbar.takeover-failure'));
            }
          )
        },
        (error) => {
          this.$logger.error("Failed taking over process job with code " +
              this.$route.params.code +" and jobId " + this.takeoverJobId + " , because: " + error);
          Utils.showFailureSnackbar(this.$t('book/public:qrcode.snackbar.takeover-failure'));
        }
      )
    },

    quickstartProcess() {
      this.$store.commit('book/process/storeUsername', null);
      this.$store.commit('book/process/storeRideCount', null);
      this.$store.commit('book/process/storeFileNumber', null);
      this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/quickstart/${this.facility.id}/${this.selectedProcess}`);
    },

    updateOpenProcessTable(options, search) {
      if(!search) search = '';
      this.$refs.tableOpenProcess.loading = true;

      //only load process job information if user is logged in
      if(!this.isLoggedIn){
        return;
      } else {
        QRCodeService.readPageableJobsFromPublic(
          this.$route.params.code,
          options.page - 1,
          options.itemsPerPage,
          search,
          options.sortBy,
          options.sortDesc,
          (resp) => {
            resp.data.content.forEach((process) => {
              if(process.accepted) {
                process.status = this.$t('book/public:qrcode.data.job.badges.status-accepted');
                process.badgeColor = "success";
              } else {
                process.status = this.$t('book/public:qrcode.data.job.badges.status-not-accepted');
                process.badgeColor = "warning";
              }
            });

            if(this.$refs.tableOpenProcess !== undefined) {
              this.$refs.tableOpenProcess.fillTableData(resp.data);
            }
          },
          (error) => {
            this.$refs.tableOpenProcess.loading = false;
            this.$logger.error("Failed loading jobs for code " + this.$route.params.code +", because: " + error);
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/public:qrcode.snackbar.job')}))
          }
        )
      }
    },

    updateProcessResultTable(options, search) {
      if(!search) search = '';
      this.$refs.tableProcessResult.loading = true;
      QRCodeService.readPageableResultsFromPublic(
        this.$route.params.code,
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        (resp) => {
          resp.data.content.forEach((process) => {
            process.finishedOn = DateUtils.parseDateTimeForDisplay(process.finishedOn);
          })

          if(this.$refs.tableProcessResult !== undefined) {
            this.$refs.tableProcessResult.fillTableData(resp.data);
          }
        },
        (error) => {
          this.$refs.tableProcessResult.loading = false;
          this.$logger.error("Failed loading jobs for code " + this.$route.params.code +", because: " + error);
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/public:qrcode.snackbar.process-result')}))
        }
      )
    }
  }
}
</script>
